import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Logo from "@/components/Logo";
import BottomNav from "@/components/BottomNav";
import {
  LayoutDashboard, UserCheck, Store, Users, ScrollText, Send, BadgeCheck, CalendarX, Headset,
  MapPin, Wallet, Calendar, Search, Check, X, ChevronDown, ChevronUp, Star, Percent, Shield, Activity,
  Mail, Phone, Languages, LogOut, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { CITIES, CATEGORIES, FULL_ACTIVITIES, SUPPORT_EMAIL } from "@/lib/constants";
import { useAuth } from "@/lib/auth";

/* ---------------- shared helpers ---------------- */

const dateOnly = (iso) => (iso || "").slice(0, 10) || "—";

function StatusBadge({ tone, children, testId }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700",
    solid: "bg-emerald-600 text-white",
    amber: "bg-amber-50 text-amber-700",
    gray: "bg-zinc-100 text-zinc-600",
    dark: "bg-zinc-200 text-zinc-500",
    red: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
  };
  return <span data-testid={testId} className={`pill whitespace-nowrap ${tones[tone] || tones.gray}`}>{children}</span>;
}

function EmptyState({ text, testId }) {
  return (
    <div data-testid={testId} className="card-surface p-8 text-center text-sm text-zinc-500">{text}</div>
  );
}

function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative flex-1 min-w-[180px]">
      <Search className="h-4 w-4 text-zinc-400 absolute top-1/2 -translate-y-1/2 start-3" />
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 rounded-xl border border-zinc-200 bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none ps-9 pe-3 text-sm"
      />
    </div>
  );
}

function SelectBox({ value, onChange, options, allLabel, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 rounded-xl border border-zinc-200 bg-white focus:border-emerald-600 outline-none px-2.5 text-sm text-zinc-700"
    >
      <option value="">{allLabel}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Chips({ options, value, onChange, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`${testPrefix}-${o.value || "all"}`}
          onClick={() => onChange(o.value)}
          className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
            value === o.value ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:border-emerald-300"
          }`}
        >
          {o.label}{typeof o.count === "number" ? ` (${o.count})` : ""}
        </button>
      ))}
    </div>
  );
}

function Modal({ open, onClose, title, children, testId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl p-5 rise">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-extrabold text-[#12211D]">{title}</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full grid place-items-center hover:bg-zinc-100" data-testid={`${testId}-close`}>
            <X className="h-4 w-4 text-zinc-500" />
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

/* ---------------- main page ---------------- */

export default function AdminDashboard() {
  const { lang, setLang, tCat, tCity, tAct } = useLang();
  const { logout } = useAuth();
  const L = (ar, en) => (lang === "ar" ? ar : en);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t2) => setParams(t2 === "overview" ? {} : { tab: t2 });

  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, m, c, r, o, ord] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users?role=merchant"),
        api.get("/admin/users?role=customer"),
        api.get("/admin/requests"),
        api.get("/admin/offers"),
        api.get("/admin/orders"),
      ]);
      setStats(s.data); setMerchants(m.data); setCustomers(c.data);
      setRequests(r.data); setOffers(o.data); setOrders(ord.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pendingList = merchants.filter((m) => !m.approved && !m.rejected);

  const SECTIONS = [
    { key: "overview", label: "نظرة عامة", en: "Overview", icon: LayoutDashboard },
    { key: "pending", label: "طلبات اعتماد التجار", en: "Merchant approvals", icon: UserCheck, badge: pendingList.length },
    { key: "merchants", label: "التجار المعتمدون", en: "Approved merchants", icon: Store },
    { key: "customers", label: "العملاء", en: "Customers", icon: Users },
    { key: "requests", label: "طلبات العملاء", en: "Customer requests", icon: ScrollText },
    { key: "offers", label: "العروض المقدمة", en: "Submitted offers", icon: Send },
    { key: "accepted", label: "الطلبات المقبولة", en: "Accepted requests", icon: BadgeCheck },
    { key: "expired", label: "الطلبات المنتهية", en: "Expired requests", icon: CalendarX },
    { key: "support", label: "الدعم", en: "Support", icon: Headset },
  ];
  const current = SECTIONS.find((s) => s.key === tab) || SECTIONS[0];

  return (
    <div className="w-full min-h-screen bg-[#F4F8F6]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-zinc-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Logo variant="mark" className="h-9 w-9" asLink={false} testId="admin-logo" />
            <div>
              <div className="font-display text-base font-extrabold text-[#12211D]">{L("لوحة التحكم", "Admin console")}</div>
              <div className="text-[10px] text-zinc-500">Hustlers KSA</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button data-testid="admin-lang-toggle" onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-zinc-100 hover:bg-zinc-200 text-xs font-semibold text-zinc-800 transition">
              <Languages className="h-3.5 w-3.5" />{lang === "en" ? "ع" : "EN"}
            </button>
            <button data-testid="admin-logout" onClick={logout}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-zinc-200 hover:bg-zinc-50 text-xs font-semibold text-zinc-600 transition">
              <LogOut className="h-3.5 w-3.5" />{L("خروج", "Logout")}
            </button>
          </div>
        </div>
        {/* Mobile nav chips */}
        <div className="lg:hidden overflow-x-auto border-t border-zinc-100" data-testid="admin-mobile-nav">
          <div className="flex gap-1.5 px-4 py-2 w-max">
            {SECTIONS.map((s) => (
              <button key={s.key} data-testid={`admin-nav-${s.key}`} onClick={() => setTab(s.key)}
                className={`relative inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold whitespace-nowrap transition ${
                  tab === s.key ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                }`}>
                <s.icon className="h-3.5 w-3.5" />{L(s.label, s.en)}
                {s.badge > 0 && (
                  <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold grid place-items-center ${tab === s.key ? "bg-white text-emerald-700" : "bg-rose-500 text-white"}`}>
                    {s.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto flex gap-5 px-4 py-5 pb-24 lg:pb-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-60 shrink-0" data-testid="admin-sidebar">
          <nav className="sticky top-24 card-surface p-2 space-y-0.5">
            {SECTIONS.map((s) => (
              <button key={s.key} data-testid={`admin-side-${s.key}`} onClick={() => setTab(s.key)}
                className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-start transition ${
                  tab === s.key ? "bg-emerald-600 text-white" : "text-zinc-600 hover:bg-emerald-50 hover:text-emerald-800"
                }`}>
                <s.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{L(s.label, s.en)}</span>
                {s.badge > 0 && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold grid place-items-center ${tab === s.key ? "bg-white text-emerald-700" : "bg-rose-500 text-white"}`} data-testid="pending-badge">
                    {s.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <h1 className="font-display text-xl font-extrabold text-[#12211D] mb-4" data-testid="admin-section-title">
            {L(current.label, current.en)}
          </h1>
          {loading ? (
            <div className="card-surface p-8 text-center text-sm text-zinc-500">…</div>
          ) : (
            <>
              {tab === "overview" && <Overview stats={stats} setTab={setTab} L={L} reload={load} />}
              {tab === "pending" && <PendingMerchants list={pendingList} L={L} tCat={tCat} tCity={tCity} tAct={tAct} reload={load} />}
              {tab === "merchants" && <MerchantsSection merchants={merchants} L={L} tCat={tCat} tCity={tCity} reload={load} />}
              {tab === "customers" && <CustomersSection customers={customers} L={L} />}
              {tab === "requests" && <RequestsSection requests={requests} L={L} tCat={tCat} tCity={tCity} tAct={tAct} />}
              {tab === "offers" && <OffersSection offers={offers} L={L} tCat={tCat} tCity={tCity} tAct={tAct} />}
              {tab === "accepted" && <AcceptedSection requests={requests} orders={orders} L={L} tCat={tCat} tCity={tCity} tAct={tAct} reload={load} />}
              {tab === "expired" && <ExpiredSection requests={requests} L={L} tCat={tCat} tCity={tCity} tAct={tAct} />}
              {tab === "support" && <SupportSection L={L} />}
            </>
          )}
        </main>
      </div>
      <div className="lg:hidden"><BottomNav /></div>
    </div>
  );
}

/* ---------------- 1. Overview ---------------- */

function Overview({ stats, setTab, L, reload }) {
  const [rate, setRate] = useState(null);
  useEffect(() => { api.get("/admin/commission").then((r) => setRate(r.data.rate)).catch(() => {}); }, []);
  const saveRate = async () => {
    try { await api.put("/admin/commission", { rate: parseFloat(rate) }); toast.success(L("تم تحديث العمولة", "Commission updated")); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cards = [
    { key: "pending", label: "تجار بانتظار الاعتماد", en: "Pending merchants", value: stats?.pending_merchants, icon: UserCheck, hot: (stats?.pending_merchants || 0) > 0 },
    { key: "merchants", label: "التجار المعتمدون", en: "Approved merchants", value: stats?.approved_merchants, icon: Store },
    { key: "customers", label: "العملاء", en: "Customers", value: stats?.customers, icon: Users },
    { key: "requests", label: "الطلبات النشطة", en: "Active requests", value: stats?.active_requests, icon: ScrollText },
    { key: "offers", label: "العروض المقدمة", en: "Submitted offers", value: stats?.offers, icon: Send },
    { key: "accepted", label: "الطلبات المقبولة", en: "Accepted requests", value: stats?.accepted_requests, icon: BadgeCheck },
    { key: "expired", label: "الطلبات المنتهية", en: "Expired requests", value: stats?.expired_requests, icon: CalendarX },
  ];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {cards.map((c, i) => (
          <button key={c.key} data-testid={`overview-card-${c.key}`} onClick={() => setTab(c.key)}
            className={`card-surface p-4 text-start transition hover:-translate-y-0.5 rise rise-${Math.min(i, 4)} ${c.hot ? "border-emerald-500 ring-1 ring-emerald-500/30" : ""}`}>
            <div className="flex items-center justify-between">
              <c.icon className={`h-5 w-5 ${c.hot ? "text-emerald-600" : "text-zinc-400"}`} />
              {c.hot && <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />}
            </div>
            <div className="mt-2 font-display text-2xl font-extrabold text-[#12211D]">{c.value ?? "—"}</div>
            <div className="text-xs text-zinc-500 mt-0.5 font-semibold">{L(c.label, c.en)}</div>
          </button>
        ))}
      </div>

      {/* Commission (existing setting) */}
      <div className="card-surface p-4" data-testid="commission-card">
        <div className="flex items-center gap-2 text-zinc-500">
          <Percent className="h-4 w-4" /><span className="label-caps text-[10px]">{L("نسبة العمولة (%)", "Commission rate (%)")}</span>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input data-testid="commission-input" type="number" min="0" max="100" value={rate ?? ""}
            onChange={(e) => setRate(e.target.value)}
            className="h-10 w-24 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-3 text-sm" />
          <button data-testid="commission-save" onClick={saveRate} className="btn-primary px-5 !py-2 text-sm">{L("تحديث", "Update")}</button>
          <span className="ms-auto text-xs text-zinc-500">{L("العمولة المتوقعة", "Expected commission")}: <b>{stats?.expected_commission ?? 0}</b> {L("ر.س", "SAR")}</span>
        </div>
      </div>
    </div>
  );
}

/* ---------------- 2. Pending merchant approvals ---------------- */

function CoverageInfo({ m, L }) {
  return (
    <div className="text-xs text-zinc-600">
      <span className="font-bold text-zinc-800">{L("نطاق التغطية:", "Coverage:")} </span>
      {m.coverage === "districts" ? L("أحياء محددة", "Specific districts") : L("كامل المدينة", "Entire city")}
      {m.coverage === "districts" && (m.districts || []).length > 0 && (
        <span className="inline-flex flex-wrap gap-1 ms-1.5 align-middle">
          {(m.districts || []).map((d) => (
            <span key={d} className="rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold">{d}</span>
          ))}
        </span>
      )}
    </div>
  );
}

function MerchantDetails({ m, L, tCat, tAct }) {
  return (
    <div className="mt-3 rounded-xl bg-zinc-50 border border-zinc-100 p-3 space-y-2 text-xs text-zinc-600" data-testid={`merchant-details-${m.id}`}>
      {m.description && <p className="text-zinc-700">{m.description}</p>}
      <div><span className="font-bold text-zinc-800">{L("واتساب:", "WhatsApp:")} </span>{m.whatsapp || "—"}</div>
      <div>
        <span className="font-bold text-zinc-800">{L("الفئات والأنشطة:", "Categories & activities:")}</span>
        {(m.categories || []).map((c) => {
          const catFull = FULL_ACTIVITIES[c] || [];
          const mine = (m.activities || []).filter((a) => catFull.includes(a));
          return (
            <div key={c} className="mt-1">
              <span className="font-semibold text-emerald-800">{tCat(c)}:</span>{" "}
              {mine.length > 0 ? mine.map(tAct).join("، ") : "—"}
            </div>
          );
        })}
      </div>
      {(m.pending_verification_activities || []).length > 0 && (
        <div className="text-amber-700 font-semibold" data-testid={`pending-vet-${m.id}`}>
          {L("بانتظار توثيق الترخيص:", "Pending license verification:")} {(m.pending_verification_activities || []).map(tAct).join("، ")}
        </div>
      )}
      <div><span className="font-bold text-zinc-800">{L("الحالة:", "Availability:")} </span>{m.availability !== false ? L("متاح لاستقبال الطلبات", "Available") : L("غير متاح حاليًا", "Unavailable")}</div>
    </div>
  );
}

function PendingMerchants({ list, L, tCat, tCity, tAct, reload }) {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [confirmM, setConfirmM] = useState(null);
  const [rejectM, setRejectM] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => list
    .filter((m) => !city || m.city === city)
    .filter((m) => !cat || (m.categories || []).includes(cat))
    .filter((m) => {
      if (!q) return true;
      const s = q.toLowerCase();
      return [m.business_name, m.name, m.email, m.phone, m.whatsapp].some((v) => (v || "").toLowerCase().includes(s));
    })
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")), [list, q, city, cat]);

  const approve = async () => {
    if (!confirmM) return;
    setBusy(true);
    try {
      await api.post(`/admin/merchants/${confirmM.id}/approve`);
      toast.success(L(`تم اعتماد ${confirmM.business_name || confirmM.name}`, "Merchant approved"));
      setConfirmM(null);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectM) return;
    if (reason.trim().length < 2) { toast.error(L("اكتب سبب الرفض", "Enter a rejection reason")); return; }
    setBusy(true);
    try {
      await api.post(`/admin/merchants/${rejectM.id}/reject`, { reason: reason.trim() });
      toast.success(L("تم رفض الطلب", "Merchant rejected"));
      setRejectM(null); setReason("");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <SearchBox value={q} onChange={setQ} placeholder={L("ابحث بالاسم أو البريد أو الجوال...", "Search name, email, phone...")} testId="pending-search" />
        <SelectBox value={city} onChange={setCity} allLabel={L("كل المدن", "All cities")} testId="pending-city-filter"
          options={CITIES.map((c) => ({ value: c, label: tCity(c) }))} />
        <SelectBox value={cat} onChange={setCat} allLabel={L("كل الفئات", "All categories")} testId="pending-cat-filter"
          options={CATEGORIES.map((c) => ({ value: c, label: tCat(c) }))} />
      </div>

      {filtered.length === 0 && <EmptyState testId="pending-empty" text={L("لا توجد طلبات اعتماد حالياً.", "No pending approvals right now.")} />}

      {filtered.map((m) => (
        <div key={m.id} className="card-surface p-4" data-testid={`pending-merchant-${m.id}`}>
          <div className="flex items-start gap-3">
            {m.logo ? <img src={m.logo} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" /> :
              <div className="h-12 w-12 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center font-bold shrink-0">{(m.business_name || m.name || "؟")[0]}</div>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-bold text-zinc-900">{m.business_name || m.name}</div>
                <StatusBadge tone="amber" testId={`pending-status-${m.id}`}>{L("قيد المراجعة", "Under review")}</StatusBadge>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</span>
                {m.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{m.phone}</span>}
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(m.city)}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{L("سجّل في", "Joined")} {dateOnly(m.created_at)}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(m.categories || []).map((c) => (
                  <span key={c} className="rounded-full bg-emerald-600/10 text-emerald-800 px-2.5 py-0.5 text-[11px] font-bold">{tCat(c)}</span>
                ))}
                <StatusBadge tone={m.availability !== false ? "green" : "gray"}>
                  {m.availability !== false ? L("متاح", "Available") : L("غير متاح", "Unavailable")}
                </StatusBadge>
              </div>
              <div className="mt-2"><CoverageInfo m={m} L={L} /></div>
            </div>
          </div>

          {expanded === m.id && <MerchantDetails m={m} L={L} tCat={tCat} tAct={tAct} />}

          <div className="mt-3 flex gap-2 flex-wrap">
            <button data-testid={`details-${m.id}`} onClick={() => setExpanded(expanded === m.id ? null : m.id)}
              className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-4 py-2 text-xs font-bold text-zinc-600 hover:bg-zinc-50 transition">
              {expanded === m.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {L("عرض التفاصيل", "View details")}
            </button>
            <button data-testid={`approve-${m.id}`} onClick={() => setConfirmM(m)}
              className="btn-primary flex-1 !py-2 text-sm min-w-[120px]">
              <Check className="h-4 w-4" />{L("اعتماد", "Approve")}
            </button>
            <button data-testid={`reject-${m.id}`} onClick={() => { setRejectM(m); setReason(""); }}
              className="inline-flex items-center justify-center gap-1 rounded-full border border-rose-300 text-rose-700 px-4 py-2 text-sm font-bold hover:bg-rose-50 transition flex-1 min-w-[120px]">
              <X className="h-4 w-4" />{L("رفض", "Reject")}
            </button>
          </div>
        </div>
      ))}

      {/* Approve confirmation */}
      <Modal open={!!confirmM} onClose={() => setConfirmM(null)} title={L("تأكيد الاعتماد", "Confirm approval")} testId="approve-modal">
        <p className="text-sm text-zinc-600">
          {L(`هل تريد اعتماد "${confirmM?.business_name || confirmM?.name}"؟ سيبدأ باستقبال الطلبات المطابقة فورًا.`,
             `Approve "${confirmM?.business_name || confirmM?.name}"? They will start receiving matching requests immediately.`)}
        </p>
        <div className="mt-4 flex gap-2">
          <button data-testid="approve-confirm" onClick={approve} disabled={busy} className="btn-primary flex-1 !py-2.5 text-sm">
            {busy ? "..." : L("نعم، اعتماد", "Yes, approve")}
          </button>
          <button onClick={() => setConfirmM(null)} className="flex-1 rounded-full border border-zinc-200 text-zinc-600 text-sm font-bold hover:bg-zinc-50 transition">
            {L("إلغاء", "Cancel")}
          </button>
        </div>
      </Modal>

      {/* Reject with reason */}
      <Modal open={!!rejectM} onClose={() => setRejectM(null)} title={L("رفض الطلب", "Reject merchant")} testId="reject-modal">
        <p className="text-sm text-zinc-600">
          {L(`اكتب سبب رفض "${rejectM?.business_name || rejectM?.name}" — سيتم إشعار التاجر بالسبب.`,
             `Enter the rejection reason for "${rejectM?.business_name || rejectM?.name}" — the merchant will be notified.`)}
        </p>
        <textarea
          data-testid="reject-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={L("مثال: بيانات النشاط غير مكتملة", "e.g. incomplete business details")}
          className="mt-3 w-full rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 outline-none px-3 py-2 text-sm"
        />
        <div className="mt-3 flex gap-2">
          <button data-testid="reject-confirm" onClick={reject} disabled={busy || reason.trim().length < 2}
            className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold py-2.5 transition disabled:opacity-50">
            {busy ? "..." : L("تأكيد الرفض", "Confirm rejection")}
          </button>
          <button onClick={() => setRejectM(null)} className="flex-1 rounded-full border border-zinc-200 text-zinc-600 text-sm font-bold hover:bg-zinc-50 transition">
            {L("إلغاء", "Cancel")}
          </button>
        </div>
      </Modal>
    </div>
  );
}

/* ---------------- 3. Approved merchants ---------------- */

function MerchantsSection({ merchants, L, tCat, tCity, reload }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("approved");
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");

  const filtered = useMemo(() => merchants
    .filter((m) => status === "approved" ? m.approved : status === "rejected" ? m.rejected : true)
    .filter((m) => !city || m.city === city)
    .filter((m) => !cat || (m.categories || []).includes(cat))
    .filter((m) => {
      if (!q) return true;
      const s = q.toLowerCase();
      return [m.business_name, m.name, m.email, m.phone].some((v) => (v || "").toLowerCase().includes(s));
    }), [merchants, q, status, city, cat]);

  const toggleFeatured = async (id) => {
    try {
      const { data } = await api.post(`/admin/merchants/${id}/featured`);
      toast.success(data.featured ? L("تم تمييز التاجر", "Marked featured") : L("أُلغي التمييز", "Unmarked"));
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const verifyVet = async (id) => {
    try {
      await api.post(`/admin/merchants/${id}/verify-vet`);
      toast.success(L("تم توثيق الترخيص وتفعيل الخدمات البيطرية", "License verified — veterinary services activated"));
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3">
      <Chips value={status} onChange={setStatus} testPrefix="merchants-status" options={[
        { value: "approved", label: L("معتمد", "Approved") },
        { value: "rejected", label: L("مرفوض", "Rejected") },
        { value: "all", label: L("الكل", "All") },
      ]} />
      <div className="flex flex-wrap gap-2">
        <SearchBox value={q} onChange={setQ} placeholder={L("ابحث عن تاجر...", "Search merchants...")} testId="merchants-search" />
        <SelectBox value={city} onChange={setCity} allLabel={L("كل المدن", "All cities")} testId="merchants-city-filter"
          options={CITIES.map((c) => ({ value: c, label: tCity(c) }))} />
        <SelectBox value={cat} onChange={setCat} allLabel={L("كل الفئات", "All categories")} testId="merchants-cat-filter"
          options={CATEGORIES.map((c) => ({ value: c, label: tCat(c) }))} />
      </div>

      {filtered.length === 0 && <EmptyState testId="merchants-empty" text={L("لا يوجد تجار في هذه القائمة.", "No merchants in this list.")} />}

      {filtered.map((m) => (
        <div key={m.id} className="card-surface p-4" data-testid={`merchant-row-${m.id}`}>
          <div className="flex items-start gap-3">
            {m.logo ? <img src={m.logo} alt="" className="h-11 w-11 rounded-xl object-cover shrink-0" /> :
              <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center font-bold shrink-0">{(m.business_name || m.name || "؟")[0]}</div>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-bold text-zinc-900 inline-flex items-center gap-1.5">
                  {m.business_name || m.name}
                  {m.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-current" />}
                </div>
                <StatusBadge tone={m.approved ? "green" : m.rejected ? "red" : "amber"}>
                  {m.approved ? L("معتمد", "Approved") : m.rejected ? L("مرفوض", "Rejected") : L("قيد المراجعة", "Under review")}
                </StatusBadge>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                <span>{m.email}</span>
                {m.phone && <span>{m.phone}</span>}
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(m.city)}</span>
                <span>{(m.categories || []).map(tCat).join(" · ")}</span>
              </div>
              <div className="mt-1.5"><CoverageInfo m={m} L={L} /></div>
              {(m.pending_verification_activities || []).length > 0 && (
                <div className="mt-1.5 text-xs text-amber-700 font-semibold" data-testid={`merchant-pending-vet-${m.id}`}>
                  {L("بانتظار توثيق الترخيص:", "Pending license verification:")} {(m.pending_verification_activities || []).join("، ")}
                </div>
              )}
              {m.rejected && m.rejection_reason && (
                <div className="mt-1.5 text-xs text-rose-600">{L("سبب الرفض:", "Rejection reason:")} {m.rejection_reason}</div>
              )}
            </div>
          </div>
          {m.approved && (
            <div className="mt-3 flex justify-end gap-2 flex-wrap">
              {(m.pending_verification_activities || []).length > 0 && (
                <button data-testid={`verify-vet-${m.id}`} onClick={() => verifyVet(m.id)}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold border border-emerald-400 text-emerald-700 hover:bg-emerald-50 transition">
                  <BadgeCheck className="h-3.5 w-3.5" />
                  {L("توثيق الترخيص البيطري", "Verify vet license")}
                </button>
              )}
              <button data-testid={`featured-${m.id}`} onClick={() => toggleFeatured(m.id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold transition ${
                  m.featured ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-amber-300 text-amber-700 hover:bg-amber-50"
                }`}>
                <Star className={`h-3.5 w-3.5 ${m.featured ? "fill-current" : ""}`} />
                {m.featured ? L("مميز", "Featured") : L("تمييز التاجر", "Mark featured")}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------- 4. Customers ---------------- */

function CustomersSection({ customers, L }) {
  const [q, setQ] = useState("");
  const filtered = customers.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [c.name, c.email, c.phone].some((v) => (v || "").toLowerCase().includes(s));
  });
  return (
    <div className="space-y-3">
      <SearchBox value={q} onChange={setQ} placeholder={L("ابحث عن عميل...", "Search customers...")} testId="customers-search" />
      {filtered.length === 0 && <EmptyState testId="customers-empty" text={L("لا يوجد عملاء بعد.", "No customers yet.")} />}
      {filtered.map((c) => (
        <div key={c.id} className="card-surface p-4 flex items-center gap-3" data-testid={`customer-row-${c.id}`}>
          <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center font-bold shrink-0">{(c.name || "؟")[0]}</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-zinc-900 text-sm">{c.name}</div>
            <div className="text-xs text-zinc-500 flex flex-wrap gap-x-3">
              <span>{c.email}</span>
              {c.phone && <span>{c.phone}</span>}
            </div>
          </div>
          <div className="text-[11px] text-zinc-400 shrink-0">{L("انضم", "Joined")} {dateOnly(c.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- request card (shared) ---------------- */

function RequestCard({ r, L, tCat, tCity, tAct, statusBadge, extra }) {
  return (
    <div className="card-surface p-4" data-testid={`admin-req-${r.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-bold text-zinc-900 text-sm">{tCat(r.category)}{r.activity ? ` · ${tAct(r.activity)}` : ""}</div>
        {statusBadge}
      </div>
      <p className="text-xs text-zinc-600 line-clamp-2 mt-1">{r.description}</p>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
        <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{r.customer_name}</span>
        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(r.city)}{r.district ? ` — ${r.district}` : ""}</span>
        <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{r.budget} {L("ر.س", "SAR")}</span>
        <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{r.deadline}</span>
        <span className="inline-flex items-center gap-1"><Send className="h-3 w-3" />{r.offer_count ?? 0} {L("عروض", "offers")}</span>
        <span className="inline-flex items-center gap-1 text-zinc-400"><Clock className="h-3 w-3" />{dateOnly(r.created_at)}</span>
      </div>
      {extra}
    </div>
  );
}

function reqStatusBadge(r, L) {
  const es = r.effective_status || r.status;
  if (es === "expired") return <StatusBadge tone="dark">{L("منتهي", "Expired")}</StatusBadge>;
  if (es === "sent") return <StatusBadge tone="sky">{L("بانتظار العروض", "Awaiting offers")}</StatusBadge>;
  if (es === "offers_received") return <StatusBadge tone="green">{L("وصلت عروض", "Offers arrived")}</StatusBadge>;
  if (es === "accepted") return <StatusBadge tone="solid">{L("تم قبول عرض", "Offer accepted")}</StatusBadge>;
  if (es === "completed") return <StatusBadge tone="gray">{L("مكتمل", "Completed")}</StatusBadge>;
  return <StatusBadge tone="gray">{es}</StatusBadge>;
}

/* ---------------- 5. Customer requests ---------------- */

function RequestsSection({ requests, L, tCat, tCity, tAct }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");

  const match = (r) => {
    const es = r.effective_status || r.status;
    if (status === "active") return r.status === "open" && !r.expired;
    if (status === "awaiting") return es === "sent";
    if (status === "offers") return es === "offers_received";
    if (status === "accepted") return es === "accepted" || es === "completed";
    if (status === "expired") return es === "expired";
    return true;
  };
  const filtered = requests
    .filter(match)
    .filter((r) => !city || r.city === city)
    .filter((r) => !cat || r.category === cat)
    .filter((r) => !q || (r.description || "").toLowerCase().includes(q.toLowerCase()) || (r.customer_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3">
      <Chips value={status} onChange={setStatus} testPrefix="req-status" options={[
        { value: "all", label: L("الكل", "All") },
        { value: "active", label: L("نشط", "Active") },
        { value: "awaiting", label: L("بانتظار العروض", "Awaiting offers") },
        { value: "offers", label: L("وصلت عروض", "Offers arrived") },
        { value: "accepted", label: L("تم قبول عرض", "Accepted") },
        { value: "expired", label: L("منتهي", "Expired") },
      ]} />
      <div className="flex flex-wrap gap-2">
        <SearchBox value={q} onChange={setQ} placeholder={L("ابحث في الطلبات...", "Search requests...")} testId="requests-search" />
        <SelectBox value={city} onChange={setCity} allLabel={L("كل المدن", "All cities")} testId="requests-city-filter"
          options={CITIES.map((c) => ({ value: c, label: tCity(c) }))} />
        <SelectBox value={cat} onChange={setCat} allLabel={L("كل الفئات", "All categories")} testId="requests-cat-filter"
          options={CATEGORIES.map((c) => ({ value: c, label: tCat(c) }))} />
      </div>
      {filtered.length === 0 && <EmptyState testId="requests-empty" text={L("لا توجد طلبات مطابقة.", "No matching requests.")} />}
      {filtered.map((r) => (
        <RequestCard key={r.id} r={r} L={L} tCat={tCat} tCity={tCity} tAct={tAct} statusBadge={reqStatusBadge(r, L)} />
      ))}
    </div>
  );
}

/* ---------------- 6. Submitted offers ---------------- */

function OffersSection({ offers, L, tCat, tCity, tAct }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const filtered = offers
    .filter((o) => status === "all" || o.status === status)
    .filter((o) => !q || (o.merchant_name || "").toLowerCase().includes(q.toLowerCase()) || (o.request_description || "").toLowerCase().includes(q.toLowerCase()));

  const badge = (s) => s === "accepted" ? <StatusBadge tone="solid">{L("مقبول", "Accepted")}</StatusBadge>
    : s === "rejected" ? <StatusBadge tone="gray">{L("لم يُقبل", "Not selected")}</StatusBadge>
    : <StatusBadge tone="amber">{L("بانتظار الرد", "Pending")}</StatusBadge>;

  return (
    <div className="space-y-3">
      <Chips value={status} onChange={setStatus} testPrefix="offer-status" options={[
        { value: "all", label: L("الكل", "All") },
        { value: "pending", label: L("بانتظار الرد", "Pending") },
        { value: "accepted", label: L("مقبول", "Accepted") },
        { value: "rejected", label: L("لم يُقبل", "Not selected") },
      ]} />
      <SearchBox value={q} onChange={setQ} placeholder={L("ابحث في العروض...", "Search offers...")} testId="offers-search" />
      {filtered.length === 0 && <EmptyState testId="offers-empty" text={L("لا توجد عروض مطابقة.", "No matching offers.")} />}
      {filtered.map((o) => (
        <div key={o.id} className="card-surface p-4" data-testid={`admin-offer-${o.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-bold text-zinc-900 text-sm">{o.merchant_name}</div>
            {badge(o.status)}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {L("على طلب:", "For request:")} {o.request_category ? tCat(o.request_category) : "—"}{o.request_activity ? ` · ${tAct(o.request_activity)}` : ""}
            {o.request_city ? ` — ${tCity(o.request_city)}${o.request_district ? ` (${o.request_district})` : ""}` : ""}
          </div>
          {o.request_description && <p className="text-xs text-zinc-400 line-clamp-1 mt-0.5">{o.request_description}</p>}
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{o.price} {L("ر.س", "SAR")}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{o.delivery_time}</span>
            <span className="inline-flex items-center gap-1 text-zinc-400"><Calendar className="h-3 w-3" />{dateOnly(o.created_at)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- 7. Accepted requests ---------------- */

function AcceptedSection({ requests, orders, L, tCat, tCity, tAct, reload }) {
  const accepted = requests.filter((r) => r.status === "accepted" || r.status === "completed");
  const orderByReq = useMemo(() => Object.fromEntries(orders.map((o) => [o.request_id, o])), [orders]);

  const complete = async (orderId) => {
    try { await api.post(`/admin/orders/${orderId}/complete`); toast.success(L("تم تحديد الطلب كمكتمل", "Marked completed")); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3">
      {accepted.length === 0 && <EmptyState testId="accepted-empty" text={L("لا توجد طلبات مقبولة بعد.", "No accepted requests yet.")} />}
      {accepted.map((r) => {
        const order = orderByReq[r.id];
        return (
          <RequestCard key={r.id} r={r} L={L} tCat={tCat} tCity={tCity} tAct={tAct} statusBadge={reqStatusBadge(r, L)}
            extra={
              <div className="mt-3 rounded-xl bg-emerald-50/70 border border-emerald-100 p-3 text-xs" data-testid={`accepted-info-${r.id}`}>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-emerald-900">
                  <span><b>{L("التاجر الفائز:", "Winning merchant:")}</b> {r.accepted_offer?.merchant_name || "—"}</span>
                  <span><b>{L("السعر المقبول:", "Accepted price:")}</b> {r.accepted_offer?.price ?? "—"} {L("ر.س", "SAR")}</span>
                  <span><b>{L("تاريخ القبول:", "Accepted on:")}</b> {dateOnly(r.accepted_offer?.accepted_at)}</span>
                </div>
                {order && order.status !== "completed" && (
                  <button data-testid={`complete-order-${order.id}`} onClick={() => complete(order.id)}
                    className="btn-primary mt-2 !py-1.5 px-4 text-xs">{L("تحديد كمكتمل", "Mark completed")}</button>
                )}
              </div>
            } />
        );
      })}
    </div>
  );
}

/* ---------------- 8. Expired requests ---------------- */

function ExpiredSection({ requests, L, tCat, tCity, tAct }) {
  const expired = requests.filter((r) => r.expired);
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">{L("طلبات انتهى موعدها النهائي دون قبول عرض — للمتابعة الإدارية فقط، ولا تظهر للتجار.", "Requests whose deadline passed without acceptance — admin tracking only; hidden from merchants.")}</p>
      {expired.length === 0 && <EmptyState testId="expired-empty" text={L("لا توجد طلبات منتهية.", "No expired requests.")} />}
      {expired.map((r) => (
        <RequestCard key={r.id} r={r} L={L} tCat={tCat} tCity={tCity} tAct={tAct}
          statusBadge={<StatusBadge tone="dark">{L("منتهي", "Expired")}</StatusBadge>} />
      ))}
    </div>
  );
}

/* ---------------- 9. Support ---------------- */

function SupportSection({ L }) {
  return (
    <div className="space-y-3">
      <div className="card-surface p-5" data-testid="support-email-card">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
            <Headset className="h-6 w-6" />
          </div>
          <div>
            <div className="font-bold text-zinc-900">{L("بريد الدعم الرسمي", "Official support email")}</div>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-emerald-700 font-bold hover:underline" data-testid="admin-support-email">{SUPPORT_EMAIL}</a>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-3">
          {L("استفسارات العملاء والتجار تصل إلى هذا البريد. تأكد من متابعته بانتظام.",
             "Customer and merchant inquiries arrive at this address. Check it regularly.")}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link to="/admin/security" data-testid="cta-security" className="card-surface p-4 flex items-center gap-3 hover:border-emerald-300 transition">
          <Shield className="h-5 w-5 text-emerald-700" />
          <div>
            <div className="text-sm font-bold text-zinc-900">{L("الأمان والملكية", "Owner & security")}</div>
            <div className="text-[11px] text-zinc-500">{L("كلمة المرور، التحقق بخطوتين، بريد المالك", "Password, 2FA, owner email")}</div>
          </div>
        </Link>
        <Link to="/admin/activity" data-testid="cta-activity" className="card-surface p-4 flex items-center gap-3 hover:border-emerald-300 transition">
          <Activity className="h-5 w-5 text-emerald-700" />
          <div>
            <div className="text-sm font-bold text-zinc-900">{L("سجل النشاط", "Activity log")}</div>
            <div className="text-[11px] text-zinc-500">{L("جميع الإجراءات الإدارية موثقة", "All admin actions are logged")}</div>
          </div>
        </Link>
      </div>
    </div>
  );
}
